import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const MathShapes3D = () => {
  const mountRef = useRef(null);
  const [shape, setShape] = useState('torus');
  const [rotation, setRotation] = useState(true);
  const [color, setColor] = useState('#00ffcc');
  const [wireframe, setWireframe] = useState(false);
  const [showCode, setShowCode] = useState(true);
  const sceneRef = useRef(null);
  const meshRef = useRef(null);
  const animationRef = useRef(null);

  // Parameter für verschiedene Formen
  const [torusParams, setTorusParams] = useState({ radius: 1.5, tube: 0.5, radialSegments: 16, tubularSegments: 100 });
  const [sphereParams, setSphereParams] = useState({ radius: 1.5, widthSegments: 32, heightSegments: 32 });
  const [knotParams, setKnotParams] = useState({ radius: 1, tube: 0.3, tubularSegments: 100, radialSegments: 16, p: 2, q: 3 });
  const [parametricCode, setParametricCode] = useState(`// u und v sind Werte zwischen 0 und 1
const x = (u - 0.5) * 4;
const y = Math.sin(u * Math.PI * 4) * Math.cos(v * Math.PI * 2) * 0.5;
const z = (v - 0.5) * 4;
return new THREE.Vector3(x, y, z);`);
  const [mobiusCode, setMobiusCode] = useState(`// Möbiusband
const uu = u * 2 * Math.PI;
const vv = v * 2 - 1;
const x = (2 + vv * Math.cos(uu / 2)) * Math.cos(uu);
const y = (2 + vv * Math.cos(uu / 2)) * Math.sin(uu);
const z = vv * Math.sin(uu / 2);
return new THREE.Vector3(x, y, z);`);
  const [kleinCode, setKleinCode] = useState(`// Klein Bottle
const uu = u * 2 * Math.PI;
const vv = v * 2 * Math.PI;
const r = 2;
const x = (r + Math.cos(uu/2) * Math.sin(vv) - Math.sin(uu/2) * Math.sin(2*vv)) * Math.cos(uu);
const y = (r + Math.cos(uu/2) * Math.sin(vv) - Math.sin(uu/2) * Math.sin(2*vv)) * Math.sin(uu);
const z = Math.sin(uu/2) * Math.sin(vv) + Math.cos(uu/2) * Math.sin(2*vv);
return new THREE.Vector3(x * 0.5, y * 0.5, z * 0.5);`);

  const createParametricGeometry = (func, slices, stacks) => {
    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const indices = [];
    const uvs = [];

    for (let i = 0; i <= slices; i++) {
      const u = i / slices;
      for (let j = 0; j <= stacks; j++) {
        const v = j / stacks;
        const p = func(u, v);
        vertices.push(p.x, p.y, p.z);
        uvs.push(u, v);
      }
    }

    for (let i = 0; i < slices; i++) {
      for (let j = 0; j < stacks; j++) {
        const a = i * (stacks + 1) + j;
        const b = a + stacks + 1;
        const c = a + 1;
        const d = b + 1;

        indices.push(a, b, c);
        indices.push(b, d, c);
      }
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  };

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    mountRef.current.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0x00ff88, 1);
    pointLight1.position.set(5, 5, 5);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0xff0088, 1);
    pointLight2.position.set(-5, -5, 5);
    scene.add(pointLight2);

    createShape(shape);

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      if (meshRef.current && rotation) {
        meshRef.current.rotation.x += 0.01;
        meshRef.current.rotation.y += 0.01;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    createShape(shape);
  }, [shape, torusParams, sphereParams, knotParams, parametricCode, mobiusCode, kleinCode]);

  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.material.color.setStyle(color);
      meshRef.current.material.wireframe = wireframe;
    }
  }, [color, wireframe]);

  const createShape = (shapeType) => {
    if (!sceneRef.current) return;
    
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry.dispose();
      meshRef.current.material.dispose();
    }

    let geometry;
    
    try {
      switch(shapeType) {
        case 'torus':
          geometry = new THREE.TorusGeometry(
            torusParams.radius, 
            torusParams.tube, 
            torusParams.radialSegments, 
            torusParams.tubularSegments
          );
          break;
        case 'sphere':
          geometry = new THREE.SphereGeometry(
            sphereParams.radius, 
            sphereParams.widthSegments, 
            sphereParams.heightSegments
          );
          break;
        case 'knot':
          geometry = new THREE.TorusKnotGeometry(
            knotParams.radius, 
            knotParams.tube, 
            knotParams.tubularSegments, 
            knotParams.radialSegments, 
            knotParams.p, 
            knotParams.q
          );
          break;
        case 'dodecahedron':
          geometry = new THREE.DodecahedronGeometry(1.5);
          break;
        case 'icosahedron':
          geometry = new THREE.IcosahedronGeometry(1.5);
          break;
        case 'parametric':
          geometry = createParametricGeometry((u, v) => {
            try {
              return eval(`(function(u, v, THREE) { ${parametricCode} })`)(u, v, THREE);
            } catch (e) {
              return new THREE.Vector3(u, 0, v);
            }
          }, 50, 50);
          break;
        case 'mobius':
          geometry = createParametricGeometry((u, v) => {
            try {
              return eval(`(function(u, v, THREE) { ${mobiusCode} })`)(u, v, THREE);
            } catch (e) {
              return new THREE.Vector3(u, 0, v);
            }
          }, 50, 50);
          break;
        case 'klein':
          geometry = createParametricGeometry((u, v) => {
            try {
              return eval(`(function(u, v, THREE) { ${kleinCode} })`)(u, v, THREE);
            } catch (e) {
              return new THREE.Vector3(u, 0, v);
            }
          }, 50, 50);
          break;
        default:
          geometry = new THREE.TorusGeometry(1.5, 0.5, 16, 100);
      }

      const material = new THREE.MeshStandardMaterial({
        color: color,
        metalness: 0.3,
        roughness: 0.4,
        wireframe: wireframe
      });

      const mesh = new THREE.Mesh(geometry, material);
      meshRef.current = mesh;
      sceneRef.current.add(mesh);
    } catch (error) {
      console.error('Fehler beim Erstellen der Form:', error);
    }
  };

  const renderParameterControls = () => {
    switch(shape) {
      case 'torus':
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-bold mb-2">Torus Parameter</h3>
            <div className="space-y-2">
              <div>
                <label className="text-white text-sm">Radius: {torusParams.radius.toFixed(2)}</label>
                <input type="range" min="0.5" max="3" step="0.1" value={torusParams.radius}
                  onChange={(e) => setTorusParams({...torusParams, radius: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Tube: {torusParams.tube.toFixed(2)}</label>
                <input type="range" min="0.1" max="1.5" step="0.05" value={torusParams.tube}
                  onChange={(e) => setTorusParams({...torusParams, tube: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Radial Segments: {torusParams.radialSegments}</label>
                <input type="range" min="3" max="64" step="1" value={torusParams.radialSegments}
                  onChange={(e) => setTorusParams({...torusParams, radialSegments: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Tubular Segments: {torusParams.tubularSegments}</label>
                <input type="range" min="3" max="200" step="1" value={torusParams.tubularSegments}
                  onChange={(e) => setTorusParams({...torusParams, tubularSegments: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
            </div>
            <div className="mt-3 p-2 bg-gray-900 rounded text-xs text-green-400 font-mono">
              new THREE.TorusGeometry({torusParams.radius}, {torusParams.tube}, {torusParams.radialSegments}, {torusParams.tubularSegments})
            </div>
          </div>
        );
      case 'sphere':
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-bold mb-2">Kugel Parameter</h3>
            <div className="space-y-2">
              <div>
                <label className="text-white text-sm">Radius: {sphereParams.radius.toFixed(2)}</label>
                <input type="range" min="0.5" max="3" step="0.1" value={sphereParams.radius}
                  onChange={(e) => setSphereParams({...sphereParams, radius: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Width Segments: {sphereParams.widthSegments}</label>
                <input type="range" min="3" max="64" step="1" value={sphereParams.widthSegments}
                  onChange={(e) => setSphereParams({...sphereParams, widthSegments: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Height Segments: {sphereParams.heightSegments}</label>
                <input type="range" min="3" max="64" step="1" value={sphereParams.heightSegments}
                  onChange={(e) => setSphereParams({...sphereParams, heightSegments: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
            </div>
            <div className="mt-3 p-2 bg-gray-900 rounded text-xs text-green-400 font-mono">
              new THREE.SphereGeometry({sphereParams.radius}, {sphereParams.widthSegments}, {sphereParams.heightSegments})
            </div>
          </div>
        );
      case 'knot':
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-bold mb-2">Torusknoten Parameter</h3>
            <div className="space-y-2">
              <div>
                <label className="text-white text-sm">Radius: {knotParams.radius.toFixed(2)}</label>
                <input type="range" min="0.5" max="2" step="0.1" value={knotParams.radius}
                  onChange={(e) => setKnotParams({...knotParams, radius: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Tube: {knotParams.tube.toFixed(2)}</label>
                <input type="range" min="0.1" max="1" step="0.05" value={knotParams.tube}
                  onChange={(e) => setKnotParams({...knotParams, tube: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">P Parameter: {knotParams.p}</label>
                <input type="range" min="1" max="10" step="1" value={knotParams.p}
                  onChange={(e) => setKnotParams({...knotParams, p: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <label className="text-white text-sm">Q Parameter: {knotParams.q}</label>
                <input type="range" min="1" max="10" step="1" value={knotParams.q}
                  onChange={(e) => setKnotParams({...knotParams, q: parseInt(e.target.value)})}
                  className="w-full" />
              </div>
            </div>
            <div className="mt-3 p-2 bg-gray-900 rounded text-xs text-green-400 font-mono">
              new THREE.TorusKnotGeometry({knotParams.radius}, {knotParams.tube}, {knotParams.tubularSegments}, {knotParams.radialSegments}, {knotParams.p}, {knotParams.q})
            </div>
          </div>
        );
      case 'parametric':
      case 'mobius':
      case 'klein':
        const code = shape === 'parametric' ? parametricCode : shape === 'mobius' ? mobiusCode : kleinCode;
        const setCode = shape === 'parametric' ? setParametricCode : shape === 'mobius' ? setMobiusCode : setKleinCode;
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-bold mb-2">Code Editor (u, v ∈ [0, 1])</h3>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full h-48 bg-gray-900 text-green-400 font-mono text-sm p-2 rounded"
              spellCheck="false"
            />
            <p className="text-xs text-gray-400 mt-2">Benutze u und v (0-1) und gib THREE.Vector3 zurück</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-screen bg-gray-900 flex">
      <div className="w-80 bg-gray-800 p-4 overflow-y-auto">
        <h1 className="text-2xl font-bold text-white mb-4">3D Editor</h1>
        
        <div className="mb-4">
          <h2 className="text-lg text-white mb-2">Formen</h2>
          <div className="flex flex-col gap-2">
            {['torus', 'sphere', 'knot', 'dodecahedron', 'icosahedron', 'parametric', 'mobius', 'klein'].map(s => (
              <button
                key={s}
                onClick={() => setShape(s)}
                className={`px-4 py-2 rounded ${shape === s ? 'bg-cyan-500' : 'bg-gray-700'} text-white hover:bg-cyan-600 transition`}
              >
                {s === 'torus' ? 'Torus' : s === 'sphere' ? 'Kugel' : s === 'knot' ? 'Torusknoten' : 
                 s === 'dodecahedron' ? 'Dodekaeder' : s === 'icosahedron' ? 'Ikosaeder' :
                 s === 'parametric' ? 'Wellenform' : s === 'mobius' ? 'Möbiusband' : 'Klein Bottle'}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h2 className="text-lg text-white mb-2">Farbe</h2>
          <div className="flex flex-wrap gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-12 h-10 rounded cursor-pointer" />
            {['#00ffcc', '#ff0088', '#ffaa00', '#00ff00', '#0088ff', '#ff00ff', '#ffffff', '#ffd700'].map(c => (
              <button key={c} onClick={() => setColor(c)} className="w-10 h-10 rounded hover:opacity-80 transition" style={{backgroundColor: c}} />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 mb-4">
          <button onClick={() => setRotation(!rotation)} className={`px-4 py-2 rounded ${rotation ? 'bg-green-600' : 'bg-red-600'} text-white`}>
            {rotation ? 'Rotation ⏸' : 'Rotation ▶'}
          </button>
          <button onClick={() => setWireframe(!wireframe)} className={`px-4 py-2 rounded ${wireframe ? 'bg-purple-600' : 'bg-gray-700'} text-white`}>
            {wireframe ? 'Solid' : 'Wireframe'}
          </button>
        </div>

        {renderParameterControls()}
      </div>
      
      <div ref={mountRef} className="flex-1" />
    </div>
  );
};

export default MathShapes3D;
